<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute, type RouteLocationRaw } from "vue-router";
import { initials } from "@/components/rail/card-text";

/* A round chip with the person's initials opens a menu headed by the person's
 * name and role, then Admin and, last, Sign out. Owner 2026-09-24: the chip
 * replaces the name in the top bar; the name and role show once it is clicked.
 * Owner request 2026-09-24: the three spread along the top bar read as messy.
 * Open, close, Escape and outside-click follow WorkspaceSwitcher.vue, the one
 * other menu in the top bar, so the two behave alike. */
const props = defineProps<{ label: string; name?: string; adminTo: RouteLocationRaw | null }>();
const emit = defineEmits<{ signOut: [] }>();

/* The initials are painted by CSS, from the data attribute, rather than
 * written into the button as text: the trigger's own name is the aria-label
 * below, and a second copy of the name in its text would be read out twice.
 * The label carries the role in brackets, so it is the fallback that has to
 * lose it first. */
const chip = computed(() => initials(props.name || props.label.replace(/\s*\([^)]*\)\s*$/, "")));

const route = useRoute();
const root = ref<HTMLElement>();
const trigger = ref<HTMLButtonElement>();
const open = ref(false);

function closeMenu(): void {
  open.value = false;
  document.removeEventListener("keydown", onDocumentKeydown);
  document.removeEventListener("pointerdown", onDocumentPointerdown);
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  event.stopPropagation();
  closeMenu();
  trigger.value?.focus();
}

function onDocumentPointerdown(event: PointerEvent): void {
  if (event.target instanceof Node && root.value?.contains(event.target)) return;
  closeMenu();
}

function toggleMenu(): void {
  if (open.value) return closeMenu();
  open.value = true;
  document.addEventListener("keydown", onDocumentKeydown);
  document.addEventListener("pointerdown", onDocumentPointerdown);
}

watch(() => route.fullPath, closeMenu);
onUnmounted(closeMenu);
</script>

<template>
  <div ref="root" class="account">
    <button ref="trigger" class="account__trigger" type="button" :data-initials="chip" aria-haspopup="menu" :aria-expanded="open" :aria-label="`Account: ${label}`" :title="label" @click="toggleMenu"></button>
    <div v-if="open" class="account__menu" role="menu">
      <p class="account__who">{{ label }}</p>
      <RouterLink v-if="adminTo" class="account__item" role="menuitem" :to="adminTo">Admin</RouterLink>
      <button class="account__item sign-out" type="button" role="menuitem" @click="emit('signOut')">Sign out</button>
    </div>
  </div>
</template>

<style scoped>
.account { position: relative; }
.account__trigger { display: grid; place-items: center; width: 34px; height: 34px; padding: 0; border: 0; border-radius: 50%; background: var(--ink-2); color: var(--card); cursor: pointer; overflow: hidden; }
/* The initials are painted from the data attribute, so the button carries no
 * text of its own and the accessible name stays the aria-label. */
.account__trigger::before { content: attr(data-initials); font-size: 13px; font-weight: 600; letter-spacing: 0.02em; line-height: 1; }
.account__trigger:hover, .account__trigger[aria-expanded="true"] { background: var(--ink); }
.account__trigger:focus-visible { outline: 2px solid var(--primary-ink); outline-offset: 2px; }
.account__who { margin: 0 0 5px; padding: 6px 8px 10px; border-bottom: 1px solid var(--hair); color: var(--ink); font-size: 13.5px; font-weight: 500; white-space: nowrap; }
.account__menu { position: absolute; z-index: var(--z-raised); top: 100%; right: 0; display: grid; min-width: 160px; margin-top: 6px; padding: 6px; border: 1px solid var(--line); border-radius: var(--radius-control); background: var(--card); box-shadow: var(--shadow-pop); }
.account__item { padding: 6px 8px; border: 0; border-radius: var(--radius-chip); background: transparent; color: var(--ink); font: inherit; font-size: 13.5px; text-align: left; text-decoration: none; white-space: nowrap; cursor: pointer; }
.account__item:hover { background: var(--primary-canvas); }
/* Sign out sits last, under a hairline, apart from where the menu takes you. */
.account__item + .sign-out { margin-top: 5px; border-top: 1px solid var(--hair); border-radius: 0 0 var(--radius-chip) var(--radius-chip); padding-top: 10px; }
</style>
